/**
 * Recharts Bar implementation. Vertical and horizontal orientations.
 * Colors come from chartTheme('recharts').theme.categoricalPalette (BAR_COLORS).
 */

import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { BarChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = BarChartProps & CommonChartProps;

export function RechartsBar({ data, orientation = 'vertical', xLabel, yLabel }: Props): JSX.Element {
  const theme = chartTheme('recharts');
  const palette = theme.theme.categoricalPalette;

  const isHorizontal = orientation === 'horizontal';

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        data={data}
        layout={isHorizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={theme.axis.stroke} />
        {isHorizontal ? (
          <>
            <XAxis type="number" tick={theme.axis.tick} stroke={theme.axis.stroke} label={yLabel} />
            <YAxis type="category" dataKey="label" tick={theme.axis.tick} stroke={theme.axis.stroke} label={xLabel} width={120} />
          </>
        ) : (
          <>
            <XAxis dataKey="label" tick={theme.axis.tick} stroke={theme.axis.stroke} label={xLabel} />
            <YAxis tick={theme.axis.tick} stroke={theme.axis.stroke} label={yLabel} />
          </>
        )}
        <Tooltip
          content={({ active, payload, label }) =>
            active && payload && payload.length > 0 ? (
              <DrillAwareTooltip
                header={label as string}
                rows={payload.map((p, i) => ({
                  color: (data[i]?.color as string) ?? palette[i % palette.length],
                  label: String(p.name ?? 'value'),
                  value: p.value as number,
                }))}
              />
            ) : null
          }
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => (
            <Cell key={`${d.label}-${i}`} fill={(d.color as string) ?? palette[i % palette.length]} />
          ))}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
